import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout.js";
import { useNavigate } from "react-router-dom";

function timeAgo(date) {
  if (!date) return "Never";
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminCustomersScreen() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("https://eleven11-i6v4.onrender.com/auth/users", {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });


      
      const data = await res.json();
      setCustomers(data.users || []);
    } catch (err) {
      console.error("Failed to fetch customers:", err);
    } finally {
      setLoading(false);
    }
  };

  const openCustomer = async (customer) => {
    setSelected(customer);
    setOrdersLoading(true);
    try {
      const res = await fetch(
        `https://eleven11-i6v4.onrender.com/api/orders?userId=${customer.id}&email=${encodeURIComponent(customer.email)}`
      );
      const data = await res.json();
      setCustomerOrders(data.orders || []);
    } catch (err) {
      console.error("Failed to fetch customer orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.firstName?.toLowerCase().includes(q) ||
      c.lastName?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  });

  const totalRevenue = customers.reduce((sum, c) => sum + Number(c.totalSpend || 0), 0);
  const totalOrders = customers.reduce((sum, c) => sum + Number(c.orderCount || 0), 0);

  const FULFILLMENT_COLORS = {
    order_placed:      { bg: "#EEF2FF", color: "#4361C2" },
    payment_confirmed: { bg: "#EEF2FF", color: "#4361C2" },
    packaged:          { bg: "#FFF7E6", color: "#B45309" },
    shipped:           { bg: "#E6F0FF", color: "#1D4ED8" },
    out_for_delivery:  { bg: "#FFF0F0", color: "#DC2626" },
    delivered:         { bg: "#EDFDF4", color: "#15803D" }
  };

  const FULFILLMENT_LABELS = {
    order_placed:      "Order Placed",
    payment_confirmed: "Payment Confirmed",
    packaged:          "Packaged",
    shipped:           "Shipped",
    out_for_delivery:  "Out for Delivery",
    delivered:         "Delivered"
  };

  return (
    <AdminLayout>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <p style={styles.kicker}>ELEVEN CONTROL CENTER</p>
          <h1 style={styles.title}>Customers</h1>
        </div>
        <div style={styles.headerStats}>
          <div style={styles.statChip}>
            <span style={styles.statNum}>{customers.length}</span>
            <span style={styles.statLabel}>Total</span>
          </div>
          <div style={styles.statChip}>
            <span style={{ ...styles.statNum, color: "#10b981" }}>{totalOrders}</span>
            <span style={styles.statLabel}>Orders</span>
          </div>
          <div style={styles.statChip}>
            <span style={{ ...styles.statNum, color: "#8b5cf6" }}>
              ${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
            <span style={styles.statLabel}>Revenue</span>
          </div>
        </div>
      </div>

      <div style={styles.layout}>

        {/* Customer List */}
        <div style={styles.tableSection}>

          <div style={styles.toolbar}>
            <input
              style={styles.search}
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div style={styles.tableWrap}>
            {loading && <p style={styles.empty}>Loading customers...</p>}
            {!loading && filtered.length === 0 && (
              <p style={styles.empty}>No customers found.</p>
            )}
            {!loading && filtered.map(customer => {
              const isSelected = selected?.id === customer.id;
              const initials = `${customer.firstName?.[0] || ""}${customer.lastName?.[0] || ""}`.toUpperCase();

              return (
                <div
                  key={customer.id}
                  style={{
                    ...styles.customerRow,
                    ...(isSelected ? styles.customerRowSelected : {})
                  }}
                  onClick={() => openCustomer(customer)}
                >
                  {/* Avatar */}
                  <div style={styles.avatar}>{initials}</div>

                  {/* Main Info */}
                  <div style={styles.customerMain}>
                    <p style={styles.customerName}>
                      {customer.firstName} {customer.lastName}
                      {customer.role === "admin" && (
                        <span style={styles.adminBadge}>Admin</span>
                      )}
                    </p>
                    <p style={styles.customerEmail}>{customer.email}</p>
                  </div>

                  {/* Stats */}
                  <div style={styles.customerStats}>
                    <div style={styles.statItem}>
                      <p style={styles.statValue}>{customer.orderCount}</p>
                      <p style={styles.statKey}>Orders</p>
                    </div>
                    <div style={styles.statItem}>
                      <p style={{ ...styles.statValue, color: "#8b5cf6" }}>
                        ${Number(customer.totalSpend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </p>
                      <p style={styles.statKey}>Spent</p>
                    </div>
                  </div>

                  {/* Joined */}
                  <div style={styles.customerRight}>
                    <p style={styles.joinedLabel}>Joined</p>
                    <p style={styles.joinedDate}>{timeAgo(customer.createdAt)}</p>
                  </div>

                  <span style={styles.chevron}>›</span>
                </div>
              );
            })}
          </div>

        </div>

        {/* Customer Detail Panel */}
        {selected && (
          <div style={styles.detailPanel}>

            <div style={styles.detailHeader}>
              <div style={styles.detailAvatar}>
                {`${selected.firstName?.[0] || ""}${selected.lastName?.[0] || ""}`.toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <p style={styles.detailName}>{selected.firstName} {selected.lastName}</p>
                <p style={styles.detailEmail}>{selected.email}</p>
                <span style={{
                  ...styles.roleBadge,
                  background: selected.role === "admin" ? "#0f0f17" : "#EEF2FF",
                  color: selected.role === "admin" ? "#fff" : "#4361C2"
                }}>
                  {selected.role === "admin" ? "Admin" : "Customer"}
                </span>
              </div>
              <button style={styles.closeBtn} onClick={() => setSelected(null)}>✕</button>
            </div>

            {/* Stats */}
            <div style={styles.detailStats}>
              <div style={styles.detailStatCard}>
                <p style={styles.detailStatValue}>{selected.orderCount}</p>
                <p style={styles.detailStatLabel}>Orders</p>
              </div>
              <div style={styles.detailStatCard}>
                <p style={{ ...styles.detailStatValue, color: "#8b5cf6" }}>
                  ${Number(selected.totalSpend || 0).toFixed(2)}
                </p>
                <p style={styles.detailStatLabel}>Total Spent</p>
              </div>
              <div style={styles.detailStatCard}>
                <p style={styles.detailStatValue}>{timeAgo(selected.lastOrderDate)}</p>
                <p style={styles.detailStatLabel}>Last Order</p>
              </div>
            </div>

            {/* Account Info */}
            <div style={styles.detailSection}>
              <p style={styles.detailLabel}>ACCOUNT INFO</p>
              <div style={styles.infoRow}>
                <span style={styles.infoKey}>Member Since</span>
                <span style={styles.infoVal}>
                  {new Date(selected.createdAt).toLocaleDateString("en-US", {
                    month: "long", day: "numeric", year: "numeric"
                  })}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoKey}>Role</span>
                <span style={styles.infoVal}>{selected.role}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoKey}>Email</span>
                <span style={styles.infoVal}>{selected.email}</span>
              </div>
            </div>

            {/* Order History */}
            <div style={styles.detailSection}>
              <p style={styles.detailLabel}>ORDER HISTORY</p>

              {ordersLoading && <p style={styles.emptySmall}>Loading orders...</p>}

              {!ordersLoading && customerOrders.length === 0 && (
                <p style={styles.emptySmall}>No orders yet.</p>
              )}

              {!ordersLoading && customerOrders.map(order => {
                const stage = order.fulfillmentStatus || "order_placed";
                const badge = FULFILLMENT_COLORS[stage] || FULFILLMENT_COLORS.order_placed;
                const label = FULFILLMENT_LABELS[stage] || stage;

                return (
                  <div
                    key={order._id}
                    style={styles.orderRow}
                    onClick={() => navigate(`/admin/orders`)}
                  >
                    <div style={{ flex: 1 }}>
                      <p style={styles.orderNum}>{order.orderNumber}</p>
                      <p style={styles.orderDate}>
                        {new Date(order.createdAt).toLocaleDateString("en-US", {
                          month: "short", day: "numeric", year: "numeric"
                        })}
                      </p>
                    </div>
                    <div style={styles.orderRight}>
                      <span style={{ ...styles.statusBadge, background: badge.bg, color: badge.color }}>
                        {label}
                      </span>
                      <p style={styles.orderTotal}>${Number(order.total || 0).toFixed(2)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        )}

      </div>

    </AdminLayout>
  );
}

const styles = {

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 28
  },

  kicker: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
    color: "#9ca3af",
    textTransform: "uppercase"
  },

  title: {
    margin: "6px 0 0",
    fontSize: 36,
    fontWeight: 800,
    letterSpacing: -1,
    color: "#0f0f17"
  },

  headerStats: {
    display: "flex",
    gap: 12,
    paddingTop: 8
  },

  statChip: {
    background: "#fff",
    borderRadius: 12,
    padding: "10px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
  },

  statNum: {
    fontSize: 22,
    fontWeight: 900,
    color: "#0f0f17",
    lineHeight: 1
  },

  statLabel: {
    fontSize: 11,
    color: "#9ca3af",
    fontWeight: 600
  },

  layout: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 20,
    alignItems: "start"
  },

  tableSection: {
    background: "#fff",
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)"
  },

  toolbar: {
    padding: "20px 24px",
    borderBottom: "1px solid #f3f4f6"
  },

  search: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box"
  },

  tableWrap: {
    maxHeight: "calc(100vh - 260px)",
    overflowY: "auto"
  },

  empty: {
    textAlign: "center",
    color: "#9ca3af",
    padding: "40px 0",
    fontSize: 14
  },

  customerRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "16px 24px",
    borderBottom: "1px solid #f9fafb",
    cursor: "pointer",
    transition: "background 0.1s ease"
  },

  customerRowSelected: {
    background: "#f5f3ff"
  },

  avatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 800,
    flexShrink: 0
  },

  customerMain: {
    flex: 1
  },

  customerName: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    color: "#0f0f17",
    display: "flex",
    alignItems: "center",
    gap: 8
  },

  adminBadge: {
    background: "#0f0f17",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 7px",
    borderRadius: 999,
    letterSpacing: 0.5
  },

  customerEmail: {
    margin: "3px 0 0",
    fontSize: 13,
    color: "#9ca3af"
  },

  customerStats: {
    display: "flex",
    gap: 20
  },

  statItem: {
    textAlign: "center"
  },

  statValue: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: "#0f0f17"
  },

  statKey: {
    margin: "2px 0 0",
    fontSize: 11,
    color: "#9ca3af",
    fontWeight: 600
  },

  customerRight: {
    textAlign: "right"
  },

  joinedLabel: {
    margin: 0,
    fontSize: 11,
    color: "#9ca3af",
    fontWeight: 600
  },

  joinedDate: {
    margin: "2px 0 0",
    fontSize: 13,
    color: "#374151",
    fontWeight: 500
  },

  chevron: {
    fontSize: 20,
    color: "#d1d5db"
  },

  /* Detail Panel */
  detailPanel: {
    width: 360,
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)",
    maxHeight: "calc(100vh - 160px)",
    overflowY: "auto",
    position: "sticky",
    top: 20
  },

  detailHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    padding: "20px 20px 16px",
    borderBottom: "1px solid #f3f4f6"
  },

  detailAvatar: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 800,
    flexShrink: 0
  },

  detailName: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: "#0f0f17"
  },

  detailEmail: {
    margin: "3px 0 6px",
    fontSize: 13,
    color: "#9ca3af"
  },

  roleBadge: {
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700
  },

  closeBtn: {
    border: "none",
    background: "#f3f4f6",
    borderRadius: "50%",
    width: 28,
    height: 28,
    cursor: "pointer",
    fontSize: 13,
    color: "#6b7280",
    flexShrink: 0
  },

  detailStats: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 1,
    background: "#f3f4f6",
    borderBottom: "1px solid #f3f4f6"
  },

  detailStatCard: {
    background: "#fff",
    padding: "14px 10px",
    textAlign: "center"
  },

  detailStatValue: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: "#0f0f17"
  },

  detailStatLabel: {
    margin: "3px 0 0",
    fontSize: 11,
    color: "#9ca3af",
    fontWeight: 600
  },

  detailSection: {
    padding: "16px 20px",
    borderBottom: "1px solid #f3f4f6"
  },

  detailLabel: {
    margin: "0 0 12px",
    fontSize: 10,
    fontWeight: 900,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 1.5
  },

  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #f9fafb"
  },

  infoKey: {
    fontSize: 13,
    color: "#9ca3af",
    fontWeight: 500
  },

  infoVal: {
    fontSize: 13,
    color: "#374151",
    fontWeight: 600
  },

  emptySmall: {
    fontSize: 13,
    color: "#9ca3af",
    textAlign: "center",
    padding: "16px 0"
  },

  orderRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 0",
    borderBottom: "1px solid #f9fafb",
    cursor: "pointer"
  },

  orderNum: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: "#0f0f17"
  },

  orderDate: {
    margin: "3px 0 0",
    fontSize: 12,
    color: "#9ca3af"
  },

  orderRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4
  },

  statusBadge: {
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700
  },

  orderTotal: {
    margin: 0,
    fontSize: 14,
    fontWeight: 800,
    color: "#0f0f17"
  }
};